import React from 'react';
import { getAbbreviatedUnit } from 'helpers/unitsAbbreviation';
import FormattedNumber from 'components/FormattedNumber';
import formatNumber from 'helpers/formatNumber';
import { OperationDetails } from '../types';
import { formatDate, getDuration, parseWorkType } from '../helpers';

export interface DetailOverviewProps {
  details: OperationDetails;
}

const DetailOverview = ({ details }: DetailOverviewProps) => {
  const { days, hours, minutes } = getDuration(
    details.startDate,
    details.endDate
  );

  const hasDuration =
    !!details.endDate &&
    ((days ?? 0) > 0 || (hours ?? 0) > 0 || (minutes ?? 0) > 0);

  const formattedStartTime = formatDate(details.startDate);
  const formattedEndTime = formatDate(details.endDate);
  const formattedWorkType = parseWorkType(details.type);

  const secondBoxContent = [
    { key: 'startTime', label: 'Start Time', value: formattedStartTime },
    ...(details.endDate
      ? [{ key: 'endTime', label: 'End Time', value: formattedEndTime }]
      : []),
    { key: 'workType', label: 'Work Type', value: formattedWorkType }
  ];

  return (
    <div className="flex justify-between gap-4">
      <section className="detail-card w-full" style={{ maxWidth: '364px' }}>
        <div className="flex py-6 px-4 divide-x divide-neutral-20">
          <div className="pr-4">
            <div className="text-sm text-neutral-60 leading-1.375rem whitespace-nowrap">
              Total applied area
            </div>
            <div>
              <div className="flex flex-row gap-0.5">
                <div className="text-2xl font-semibold text-neutral-1000 leading-8">
                  {formatNumber(details.totalAppliedArea)}
                </div>
                <div className="flex" style={{ height: '31px' }}>
                  <div className="flex flex-col text-sm text-neutral-1000 justify-end leading-1.375rem">
                    {getAbbreviatedUnit(details.totalAppliedAreaUnit)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="pl-4">
            <div className="text-sm text-neutral-60 leading-1.375rem">
              Total cost
            </div>
            <div>
              <span className="text-2xl font-semibold text-neutral-1000 leading-8">
                <FormattedNumber unit="usCurrency" roundedTo={2}>
                  {Number(details.totalCost) || 0}
                </FormattedNumber>
              </span>
            </div>
          </div>
        </div>
        {hasDuration && (
          <div className="border-t border-neutral-20 flex pt-11px px-4 pb-14px items-end">
            <div className="text-sm text-neutral-60 leading-1.375rem">
              Duration
            </div>
            <div className="ml-auto flex flex-row gap-1.5">
              {days && days > 0 ? (
                <div className="flex flex-row items-end gap-0.5">
                  <div className="text-2xl font-semibold text-neutral-1000 leading-7">
                    {days}
                  </div>
                  <div className="text-sm text-neutral-1000 leading-1.375rem">
                    {days === 1 ? 'day' : 'days'}
                  </div>
                </div>
              ) : null}
              {hours && hours > 0 ? (
                <div className="flex flex-row items-end gap-0.5">
                  <div className="text-2xl font-semibold text-neutral-1000 leading-7">
                    {hours}
                  </div>{' '}
                  <div className="text-sm text-neutral-1000 leading-1.375rem">
                    hrs
                  </div>
                </div>
              ) : null}
              <div className="flex flex-row items-end gap-0.5">
                <div className="text-2xl font-semibold text-neutral-1000 leading-7">
                  {minutes}
                </div>
                <div className="text-sm text-neutral-1000 leading-1.375rem">
                  min
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      <section className="detail-card w-full px-6 py-4 flex flex-col gap-2">
        {secondBoxContent.map(({ key, label, value }) => (
          <div
            key={key}
            className="flex gap-5 pt-2 pb-7px border-b border-neutral-20 leading-5"
          >
            <div className="text-sm text-neutral-60 w-70px">{label}</div>
            <div className="text-sm text-neutral-1000">{value}</div>
          </div>
        ))}
      </section>
    </div>
  );
};

export default DetailOverview;
